/** @format */

import { useContext, useMemo } from "react";
import { PostMethod } from "../api/Api";
import { PASSWORD_REGISTERED_KEY } from "./useRedirectPasswordHooks";
import {
  getSession,
  setBankAccountInfoData,
  setMerchantInfoData,
  setOperationInfoData,
  setRepresentativeInfoData,
  setShopInfoData,
} from "../sessionStorage/sessionStorageMethod";
import { Context } from "../stores/Provider";
import { setIsLoading } from "../function/setIsLoading";
import { deleteFile } from "../function/deleteFile";
import {
  initShopSessionDataType,
  initApiModelType,
  entryInfoPostRequestDataType,
  entryInfoPostResponseDataType,
  initUserSessionDataType,
  initBankAccountSessionDataType,
  entryInfoMerchantRequestDataType,
  entryInfoShopRequestDataType,
} from "../constance/typeModel";
import { initApiResponse } from "../constance/constance";
import { paymentSiteChangeToCodeHandler } from "../function/paymentSiteToCode";

const useEntryConfirmHooks = (navigateByAgent: Function, redirect?: boolean) => {
  //セッションデータ抽出
  const userInfoData = getSession("user");
  const merchantInfoData = getSession("merchant");
  const bankAccountInfoData = getSession("bank_account");
  const representativeInfoData = getSession("representative");
  const operationInfoData = getSession("operation");
  const shopInfoData = getSession("shop");
  const reviewContentsDevideInfoData = getSession("review_contents_division");
  const economicInfoData = getSession("economic_condition");
  const systemSettingInfoData = getSession("system_setting_master");
  const filesSession = getSession("files");
  const { state, dispatch } = useContext(Context);
  const isOpenModal = state.is_open_modal;

  const zAccount = useMemo(() => {
    return state.zAccount;
  }, [state.zAccount]);

  //レスポンスをリコイルにセット
  const setRecoilDataHandler = (response: initApiModelType<entryInfoPostResponseDataType>) => {
    let merchantObj = {};
    let representativeObj = {};
    let operatorObj = {};
    let bankObj = {};
    if (response.response_data === undefined) return;
    for (let merchantItem in response.response_data.merchant) {
      if (merchantItem.split("_")[0] === "merchant") {
        merchantObj = { ...merchantObj, [merchantItem]: response.response_data.merchant[merchantItem] };
      }
      if (merchantItem.split("_")[0] === "representative") {
        representativeObj = { ...representativeObj, [merchantItem]: response.response_data.merchant[merchantItem] };
      }
      if (merchantItem.split("_")[0] === "operator" || merchantItem.split("_")[0] === "support") {
        operatorObj = { ...operatorObj, [merchantItem]: response.response_data.merchant[merchantItem] };
      }
    }
    for (let merchantItem in response.response_data.merchant) {
      if (merchantItem.split("_")[0] === "bank") {
        bankObj = { ...bankObj, [merchantItem]: response.response_data.merchant[merchantItem] };
      }
    }

    if (response.response_data.shop === undefined) return;

    setMerchantInfoData({ ...merchantInfoData, ...merchantObj });
    setRepresentativeInfoData({ ...representativeInfoData, ...representativeObj });
    setOperationInfoData({ ...operationInfoData, ...operatorObj });
    setBankAccountInfoData({ ...bankAccountInfoData, ...bankObj });
    setShopInfoData({ ...shopInfoData, ...response.response_data.shop });
  };

  type devideDataType = {
    [key: string]: string;
    antique_flag: string;
    btob_flag: string;
    estimate_flag: string;
    online_local_flag: string;
    prepaid_continuous_flag: string;
    purchase_points_flag: string;
    real_store_flag: string;
    rental_flag: string;
    reserve_item_flag: string;
    subscription_flag: string;
  };

  const setReviewContentsNullHandler = async (
    shopData: initShopSessionDataType,
    deivdeData: devideDataType,
    merchantData: { merchant_type: string },
  ): Promise<{ [key: string]: string | number | boolean | null }> => {
    const resultObj: { [key: string]: string | number | boolean | null } = {};

    const parentChildMap: {
      [flagName: string]: {
        parents: (keyof initShopSessionDataType)[];
        children?: (keyof initShopSessionDataType)[];
        grandchildren?: (keyof initShopSessionDataType)[];
        fileKey?: string;
        visibilityCondition?: () => boolean;
      };
    } = {
      antique_flag: {
        parents: ["shop_addition_antique_flag"],
        fileKey: "antique",
      },
      subscription_flag: {
        parents: ["shop_addition_subscription_flag"],
        children: ["shop_addition_subscription_code"],
      },
      reserve_item_flag: {
        parents: ["shop_addition_reserved_product_flag"],
        children: ["shop_addition_reservation_period_flag"],
      },
      rental_flag: {
        parents: ["shop_addition_rental_flag"],
        children: ["shop_addition_rental_product_url"],
        fileKey: "rental",
      },
      real_store_flag: {
        parents: ["shop_addition_physical_store_flag"],
        children: ["shop_addition_physical_store_url_flag"],
        grandchildren: ["shop_addition_physical_store_url"],
        visibilityCondition: () => String(deivdeData.real_store_flag) === "true" && merchantData.merchant_type === "2",
      },
      online_local_flag: {
        parents: ["shop_addition_online", "shop_addition_offline"],
      },
      btob_flag: {
        parents: ["shop_addition_btob_code"],
      },
      estimate_flag: {
        parents: ["shop_addition_estimated_product_flag"],
      },
      prepaid_continuous_flag: {
        parents: ["shop_addition_prepaid_flag"],
      },
      purchase_points_flag: {
        parents: ["shop_addition_point_purchase_flag"],
      },
    };

    for (const flagName in parentChildMap) {
      const { parents, children = [], grandchildren = [], fileKey, visibilityCondition } = parentChildMap[flagName];

      const isVisible = visibilityCondition?.() ?? String(deivdeData[flagName]) === "true";
      const isParentEnabled = parents.some((p) => Boolean(shopData[p]));

      for (const parent of parents) {
        resultObj[parent] = isVisible ? shopData[parent] : null;
      }

      for (const child of children) {
        resultObj[child] = isVisible && isParentEnabled ? shopData[child] : null;
      }

      for (const grandchild of grandchildren) {
        const correspondingChild = children[grandchildren.indexOf(grandchild)];
        const isChildEnabled = Boolean(shopData[correspondingChild]);
        resultObj[grandchild] = isVisible && isParentEnabled && isChildEnabled ? shopData[grandchild] : null;
      }

      if (isVisible && !isParentEnabled && fileKey) {
        await deleteFiles(fileKey);
      }
    }

    if (zAccount === "bcart") {
      resultObj.shop_addition_subscription_flag = false;
      resultObj.shop_addition_point_purchase_flag = false;
      resultObj.shop_addition_btob_code = "3";
      resultObj.shop_addition_physical_store_flag = false;
    }

    if (zAccount === "emochi") {
      resultObj.shop_addition_physical_store_flag = null;
    }

    return resultObj;
  };

  // 経済条件セット
  const setEconomicData = () => {
    let economicInsertData = economicInfoData;
    let creditSystemInsert = { ...economicInfoData.credit.system_setting };
    let bankSystemInsert = { ...economicInfoData.bank.system_setting };
    let cvsSystemInsert = { ...economicInfoData.cvs.system_setting };
    let creditEconomicInsert = { ...economicInfoData.credit.economic_requirement };
    let bankEconomicInsert = { ...economicInfoData.bank.economic_requirement };
    let cvsEconomicInsert = { ...economicInfoData.cvs.economic_requirement };
    let atobaraiEconomicInsert = { ...economicInfoData.atobarai.economic_requirement };

    // cgi_url追加
    if (systemSettingInfoData.credit_cgi_agent_flag && systemSettingInfoData.credit_cgi_url_agent !== null) {
      creditSystemInsert.cgi_url = systemSettingInfoData.credit_cgi_url_agent.replace(
        "{accountId}",
        shopInfoData.account_id,
      );
    }
    if (systemSettingInfoData.bank_cgi_agent_flag && systemSettingInfoData.bank_cgi_url_agent !== null) {
      bankSystemInsert.cgi_url = systemSettingInfoData.bank_cgi_url_agent.replace(
        "{accountId}",
        shopInfoData.account_id,
      );
    }
    if (systemSettingInfoData.cvs_cgi_agent_flag && systemSettingInfoData.cvs_cgi_url_agent !== null) {
      cvsSystemInsert.cgi_url = systemSettingInfoData.cvs_cgi_url_agent.replace("{accountId}", shopInfoData.account_id);
    }
    //支払いサイト変換
    creditEconomicInsert.payment_site = paymentSiteChangeToCodeHandler(creditEconomicInsert.payment_site);
    bankEconomicInsert.payment_site = paymentSiteChangeToCodeHandler(bankEconomicInsert.payment_site);
    cvsEconomicInsert.payment_site = paymentSiteChangeToCodeHandler(cvsEconomicInsert.payment_site);
    atobaraiEconomicInsert.payment_site = paymentSiteChangeToCodeHandler(atobaraiEconomicInsert.payment_site);

    economicInsertData = {
      ...economicInsertData,
      credit: {
        economic_requirement: creditEconomicInsert,
        system_setting: creditSystemInsert,
      },
      bank: {
        economic_requirement: bankEconomicInsert,
        system_setting: bankSystemInsert,
      },
      cvs: {
        economic_requirement: cvsEconomicInsert,
        system_setting: cvsSystemInsert,
      },
      atobarai: {
        ...economicInsertData.atobarai,
        economic_requirement: atobaraiEconomicInsert,
      },
    };
    return economicInsertData;
  };

  // API送信データを代理店とIPの有無によって変更
  const setMainData = (
    userInfoData: initUserSessionDataType,
    postStatus: string,
    merchantInsertData: entryInfoMerchantRequestDataType,
    bankAccountInfoData: initBankAccountSessionDataType,
    shopInsertData: entryInfoShopRequestDataType,
  ): entryInfoPostRequestDataType => {
    const economicInsertData = setEconomicData();
    if (userInfoData.ip_flag) {
      let mainData: entryInfoPostRequestDataType = {
        ...userInfoData,
        status: postStatus,
        merchant: {
          merchant: merchantInsertData,
          bank: {
            ...bankAccountInfoData,
            bank_account_category: null,
          },
        },
        shop: shopInsertData,
        services: economicInsertData,
        economic_condition_confirmed_flag: state.user_status.economic_condition_confirmed_flag,
      };
      return mainData;
    } else {
      let mainData: entryInfoPostRequestDataType = {
        ...userInfoData,
        status: postStatus,
        merchant: {
          merchant: merchantInsertData,
          bank: bankAccountInfoData,
        },
        shop: shopInsertData,
        services: economicInsertData,
        economic_condition_confirmed_flag: state.user_status.economic_condition_confirmed_flag,
      };
      return mainData;
    }
  };

  //ステータスをENTRY_OKに変更して、APIを投げる
  const entryComplete = async () => {
    let response: initApiModelType<entryInfoPostResponseDataType> = initApiResponse;
    const setReviewContentsNullData = await setReviewContentsNullHandler(
      shopInfoData,
      reviewContentsDevideInfoData,
      merchantInfoData,
    );
    const merchantWebsiteUrl = economicInfoData.cvs.system_setting.select_flag
      ? merchantInfoData.merchant_website_url
      : "";
    const shopNameAtobarai = economicInfoData.atobarai.system_setting.select_flag
      ? shopInfoData.shop_atobarai_customer_notice_store_name !== ""
        ? shopInfoData.shop_atobarai_customer_notice_store_name.replace(/-/g, "－")
        : ""
      : "";
    const shopCvsWebsiteName = economicInfoData.cvs.system_setting.select_flag
      ? shopInfoData.shop_cvs_website_name
      : "";
    const shopCvsWebsiteNameKana = economicInfoData.cvs.system_setting.select_flag
      ? shopInfoData.shop_cvs_website_name_kana
      : "";

    const merchantInsertData = {
      ...merchantInfoData,
      ...representativeInfoData,
      ...operationInfoData,
      merchant_website_url: merchantWebsiteUrl,
    };
    let shopInsertData = {
      ...shopInfoData,
      ...setReviewContentsNullData,
      shop_atobarai_customer_notice_store_name: shopNameAtobarai,
      shop_cvs_website_name: shopCvsWebsiteName,
      shop_cvs_website_name_kana: shopCvsWebsiteNameKana,
    };

    if (zAccount === "ocnk" && economicInfoData.cvs.system_setting.select_flag) {
      shopInsertData = {
        ...shopInsertData,
        shop_addition_price_range_max: shopInfoData.shop_addition_price_range_max,
        shop_addition_price_range_min: shopInfoData.shop_addition_price_range_min,
      };
    }

    const isOpen = shopInfoData.shop_addition_site_status_code === 1;
    const siteStatusMethod = shopInfoData.shop_addition_site_status_method;

    const clearWebsiteFields = () => {
      shopInsertData = {
        ...shopInsertData,
        shop_addition_shop_website_check_url: "",
        shop_addition_business_deal_url: "",
        shop_addition_shop_website_id: "",
        shop_addition_shop_website_password: "",
      };
    };

    const clearPartialWebsiteFields = () => {
      shopInsertData = {
        ...shopInsertData,
        shop_addition_shop_website_check_url: "",
        shop_addition_business_deal_url: "",
      };
    };

    if (isOpen) {
      // サイト状態が「オープン済み」の場合の処理
      if (zAccount === "ocnk") {
        clearWebsiteFields();
        await deleteFiles("examination_document");
      } else if (zAccount === "bcart") {
        clearPartialWebsiteFields();
      } else if (zAccount === "emochi") {
        clearWebsiteFields();
      }
    }

    if (siteStatusMethod === 0) {
      // サンプルサイト・デモサイトによる審査
      await deleteFiles("examination_document");
    } else if (siteStatusMethod === 1) {
      // 資料による審査
      clearWebsiteFields();
    }

    const postStatus = state.user_status.status === "WAITING" ? "FIXED" : "ENTRY_OK";

    const mainData = setMainData(userInfoData, postStatus, merchantInsertData, bankAccountInfoData, shopInsertData);
    //ローディング画面開始
    setIsLoading(true, dispatch);
    response = await PostMethod<entryInfoPostRequestDataType, entryInfoPostResponseDataType>(mainData, "/entry_info");
    // response = () => entryInfoPostRequestDataType

    return response;
  };

  const deleteFiles = async (fileKey: string) => {
    const files = filesSession?.[fileKey];
    if (Array.isArray(files)) {
      for (const file of files) {
        if (file?.file_id) {
          await deleteFile(userInfoData.entry_id, fileKey, file.file_id, dispatch);
        }
      }
    }
  };

  //API正常時処理
  const normal = (response: initApiModelType<entryInfoPostResponseDataType>) => {
    const navigatePath = redirect ? "/redirect_completed" : "/entry_completed";
    setRecoilDataHandler(response);
    sessionStorage.removeItem("web_entry_password");
    sessionStorage.removeItem(PASSWORD_REGISTERED_KEY);
    //ローディング画面開始
    setIsLoading(false, dispatch);
    return navigateByAgent(navigatePath);
  };
  //API異常時処理
  const abnormal = () => {
    const navigatePath = redirect ? "/redirect_confirm" : "/entry_confirm";
    dispatch({
      type: "set-modal-flag",
      payload: {
        path: "confirm",
        type: "error",
        flag: true,
        message: "申し込み登録が完了できませんでした。",
      },
    });
    //ローディング画面開始
    setIsLoading(false, dispatch);
    return navigateByAgent(navigatePath);
  };
  //ダッシュボード遷移ハンドラ
  const navigateDashboardHandler = () => {
    return navigateByAgent("/dashboard");
  };
  return { isOpenModal, entryComplete, normal, abnormal, navigateDashboardHandler, userInfoData };
};

export default useEntryConfirmHooks;
